import { type LockManager, syncLockName } from "./locks";
import {
  decideAssignmentPostOutcome,
  decideChangeRequestSetOutcome,
  decidePostOutcome,
  formatPushError,
} from "./outbox-state";
import { type EvidencePart, type StorageAdapter } from "./store-contract";
import { type HttpStatus, type TransportAdapter } from "./transport-contract";
import {
  type ChangeRequestSetCreate,
  type IntakeCandidatesRequest,
  type OutboxAttachmentRow,
  type OutboxChangeRequestSetRow,
  type OutboxSubmissionRow,
  type SyncScope,
} from "./types";

// Foreground-only sync engine, the offline half of the online-first posture:
// while connected a host's screens read the server, and a regulatory send
// resolves through the awaited `syncFresh` drain — this run's pull is the
// offline store's WRITER (it stocks the bundle so offline work has its rows),
// never the online read source, and its drain is the offline queue's replay
// loop. Triggers (app launch, foreground/focus, connectivity regain,
// post-claim, the manual affordances) coalesce on a single in-flight run that
// executes a fixed order: recover interrupted rows, push queued walk-up
// assignment creates
// FIRST, then queued assignment transitions, then change-request sets, then
// finalized submissions FIFO with their evidence uploads, then supplements,
// clean up acknowledged rows, then pull the bundle (and, on a field pull, the
// changed catalogs). Assignment-create-before-everything is deliberate — a
// submission or transition for a locally-created walk-up assignment must never
// reach the server before its assignment row exists. Push-before-pull is deliberate — a
// pull first would drop a bundle row the queued submission still references.
// Outbox rows replay against their SNAPSHOTTED stack + org; the CURRENT app
// mode only selects which bundle to pull. The run's first NETWORK act is
// transport.ensureFresh(), so a queued regulatory write never races an expired
// bearer.

export interface SyncResult {
  pulled: boolean;
  wentOffline: boolean;
}

// One upload's answer as the item's completion reads it, whichever route took it.
interface UploadAnswer {
  status: HttpStatus;
  completion: { submissionStatus: string; complete: boolean } | undefined;
}

// Mirrors decidePostOutcome's retryable arm: network failures and transient
// server conditions leave rows queued for the next trigger.
function isRetryable(status: HttpStatus): boolean {
  return status === "network" || status === 429 || status >= 500;
}

// What an upload drain sends: every file not yet acked, or — when all are —
// the last one again (a per-file idempotent 200), so a completion the server
// never reported finishes without a GET op on the field surface.
function uploadsToSend(all: OutboxAttachmentRow[]): OutboxAttachmentRow[] {
  const pending = all.filter((a) => a.state !== "uploaded");
  if (pending.length > 0) return pending;
  const replay = all[all.length - 1];
  return replay === undefined ? [] : [replay];
}

// The at-send candidate check's body for an intake the check has not settled;
// null for every other set (a picked intake is `standalone`, a dismissed one
// carries `candidates_checked_at`).
function candidateCheckRequest(row: OutboxChangeRequestSetRow): IntakeCandidatesRequest | null {
  const body = JSON.parse(row.bodyJson) as ChangeRequestSetCreate;
  const intake = body.intake;
  if (body.anchor_kind !== "intake" || intake === undefined) return null;
  if (body.candidates_checked_at !== undefined) return null;
  return {
    org_id: body.org_id,
    facility_name: intake.facility_name,
    address_line1: intake.address_line1,
    city: intake.city,
    state_code: intake.state_code,
    zip_code: intake.zip_code,
    ...(intake.meter_number !== undefined ? { meter_number: intake.meter_number } : {}),
  };
}

// The sync_meta key holding the last bundle pull's server timestamp. Org
// scope keys per org so switching orgs never shows another org's pull time.
export function syncMetaKey(scope: SyncScope): string {
  return scope.mode === "field" ? "last_sync_field" : `last_sync_org_${scope.orgId}`;
}

export interface SyncEngineDeps {
  storage: StorageAdapter;
  transport: TransportAdapter;
  locks: LockManager;
  profileId: string;
  // Sync runs only when an actor surface is active: field mode, or org mode
  // with an active org (every org member is an assignment executor). Anything
  // else skips silently.
  resolveScope: () => SyncScope | null;
}

export interface SyncEngine {
  syncNow(): Promise<SyncResult | null>;
  syncFresh(): Promise<SyncResult | null>;
  subscribeSyncState(listener: () => void): () => void;
  isSyncRunning(): boolean;
}

export function createSyncEngine(deps: SyncEngineDeps): SyncEngine {
  const { locks, profileId, resolveScope, storage, transport } = deps;

  let inFlightRun: Promise<SyncResult | null> | null = null;
  let running = false;
  const stateListeners = new Set<() => void>();

  function notifyStateListeners(): void {
    for (const listener of stateListeners) listener();
  }

  // Run-state subscription for the sync hook: notified when a run starts and
  // when it settles (queue counts move on both edges).
  function subscribeSyncState(listener: () => void): () => void {
    stateListeners.add(listener);
    return () => {
      stateListeners.delete(listener);
    };
  }

  function isSyncRunning(): boolean {
    return running;
  }

  // Single-flight entry point: concurrent triggers coalesce on the in-flight
  // promise. Resolves null when no actor surface is active or the run failed
  // unexpectedly — the queue holds and the next trigger retries.
  function syncNow(): Promise<SyncResult | null> {
    if (inFlightRun !== null) return inFlightRun;
    const scope = resolveScope();
    if (scope === null) return Promise.resolve(null);
    running = true;
    notifyStateListeners();
    inFlightRun = locks
      .withExclusiveLock(syncLockName(profileId), () => runSync(scope))
      .catch((err: unknown): null => {
        console.warn("fieldwork sync run failed", err);
        return null;
      })
      .finally(() => {
        inFlightRun = null;
        running = false;
        notifyStateListeners();
      });
    return inFlightRun;
  }

  // A run whose bundle pull is guaranteed to observe every server write made
  // before this call, and whose drain pushes every row queued before it.
  // syncNow's coalescing would hand back an in-flight run whose pull may have
  // raced AHEAD of the caller's write (e.g. an unscheduled-test create racing a
  // focus-triggered sync), or whose drain already passed the outbox before the
  // caller queued its row — either way the write waits for the next trigger.
  // When a run is in flight, chain a fresh run after it settles instead of
  // coalescing.
  function syncFresh(): Promise<SyncResult | null> {
    const current = inFlightRun;
    if (current === null) return syncNow();
    return current.then(() => syncNow());
  }

  async function runSync(scope: SyncScope): Promise<SyncResult> {
    await storage.recoverInFlight();
    // Refresh BEFORE draining. Every PUSH below is a regulatory write replaying on
    // a snapshotted stack, and a bearer that expires mid-drain turns a queue of
    // valid captures into a queue of 401s. A transport failure here means the
    // drain would fail anyway; a server rejection means the session is over and
    // the queue waits for its owner to sign back in — in neither case does a row
    // move, and neither case counts against a row's `attempts` or writes it a
    // failure reason: nothing was attempted on the wire, so nothing is recorded
    // as having failed there. Local cleanup is not gated: it deletes staged files
    // and drafts the server already acknowledged, it retries what a previous run's
    // cleanup failed to reclaim, and withholding it from an offline session is how
    // a device runs out of room for the capture it is holding.
    const { outcome } = await transport.ensureFresh();
    if (outcome !== "fresh") {
      await cleanupAcked();
      return { pulled: false, wentOffline: outcome === "offline" };
    }
    // Walk-up assignment creates replay BEFORE every transition and submission: a
    // queued submission/transition for a locally-created walk-up assignment must
    // never reach the server before its assignment row exists. A retryable
    // failure here flips online false, stopping the rest of the run cleanly.
    let online = await pushAssignments();
    if (online) online = await pushTransitions();
    // Sets after transitions: an assignment-anchored set names an assignment
    // whose in-progress transition must already be on the server.
    if (online) online = await pushChangeRequestSets();
    if (online) online = await pushSubmissions();
    // Supplements push after their parents (UUIDv7 FIFO orders parent-before-
    // supplement within one drain; the per-row server-id gate covers the
    // crash-between-pushes case). The retryable arm of pushSupplements flips
    // online false so a mid-drain disconnect stops the rest of the run cleanly.
    if (online) online = await pushSupplements();
    await cleanupAcked();
    let pulled = false;
    if (online) {
      const pull = await pullBundle(scope);
      pulled = pull.pulled;
      online = !pull.offline;
    }
    return { pulled, wentOffline: !online };
  }

  // Push queued walk-up assignment creates FIRST, FIFO by created_at. Each hop
  // re-queries so the loop drains the queue. A `synced` outcome (fresh 201 or
  // idempotent 200 replay) or a deterministic-4xx `error` advances to the next
  // row; a retryable outcome (network / 5xx / 429) re-queues that row and returns
  // false so the run goes offline and the pull is skipped — the same
  // short-circuit the other pushers use. WHY the `error` arm just continues: a
  // submission/transition whose assignment-create landed in `error` will itself
  // fail deterministically at its own POST and surface through the existing
  // submission error state — the ordering guarantee only concerns the success
  // path, so a parked create needs no special-casing downstream.
  async function pushAssignments(): Promise<boolean> {
    for (;;) {
      const row = await storage.nextQueuedAssignment();
      if (row === null) return true;
      await storage.markAssignmentSending(row.assignmentId);
      const { status, error } = await transport.createAssignment(row);
      const outcome = decideAssignmentPostOutcome(status);
      if (outcome === "synced") {
        await storage.markAssignmentSynced(row.assignmentId);
        continue;
      }
      if (outcome === "error") {
        // decideAssignmentPostOutcome routes only deterministic 4xx numbers here;
        // capture the server's rejection token so "Failed" names WHY.
        await storage.markAssignmentError(
          row.assignmentId,
          typeof status === "number" ? formatPushError(status, error) : `http_${status}`,
        );
        continue;
      }
      await storage.markAssignmentRetry(row.assignmentId);
      return false;
    }
  }

  // Returns false when connectivity is gone (the queued row stays queued).
  async function pushTransitions(): Promise<boolean> {
    for (;;) {
      const transition = await storage.nextQueuedTransition();
      if (transition === null) return true;
      const status = await transport.patchTransition(transition);
      if (status !== "unsendable" && isRetryable(status)) return false;
      if (status !== 200) {
        // 409/404 (or any other deterministic rejection): drop the optimistic
        // local status now — server truth wins on the next pull.
        await storage.setLocalStatus(transition.assignmentId, null);
      }
      await storage.markTransitionSynced(transition.assignmentId);
    }
  }

  // FIFO replay: nextPending always yields the oldest unfinished row, so each
  // successful hop re-queries. A (row, state) pair seen twice in one run made
  // no progress (the server still reports the evidence set incomplete after
  // every local file was acked) — stop and let the next run retry over spinning.
  async function pushSubmissions(): Promise<boolean> {
    const seen = new Set<string>();
    for (;;) {
      const row = await storage.nextPending();
      if (row === null) return true;
      const key = `${row.clientSubmissionId}:${row.state}`;
      if (seen.has(key)) return true;
      seen.add(key);
      const ok = row.state === "finalized" ? await postSubmission(row) : await pushUploads(row);
      if (!ok) return false;
    }
  }

  // Push queued company supplements AFTER their parent submissions. FIFO by
  // UUIDv7 client id over a snapshot of every finalized supplement so one stuck
  // parent never blocks the supplements behind it (head-of-line avoidance).
  // Returns false (the run goes offline) only on a retryable failure so the rest
  // of the drain stops cleanly; a deterministic 4xx parks that ONE supplement in
  // error and the drain continues. A parent that has not recorded its server id
  // is handled by readiness: a parent still in flight means the supplement is
  // skipped this pass (it replays next run, after FIFO pushes the parent), while
  // a parent parked in a terminal state will NEVER record an id, so the
  // supplement is parked too rather than spinning forever. A BLOCKED parent is
  // neither: the utility refused it over a curable requirement, so it is in
  // flight — the supplement is skipped this pass and pushes after the cure
  // replays the parent.
  async function pushSupplements(): Promise<boolean> {
    const pending = await storage.listPendingSupplements();
    for (const row of pending) {
      const parent = await storage.getOutboxSubmission(row.parentClientSubmissionId);
      const parentServerId = parent?.serverSubmissionId ?? null;
      if (parentServerId === null) {
        // The resolved id gates only this readiness check — it is NEVER sent on
        // the wire (the server resolves the FK from parent_client_submission_id).
        if (parent !== null && (parent.state === "error" || parent.state === "revoked")) {
          // The parent's regulatory record will never exist server-side, so the
          // supplement's parent FK can never resolve — park it instead of
          // re-skipping it on every future run.
          await storage.markSupplementError(row.clientSubmissionId, `parent_${parent.state}`);
          continue;
        }
        // Parent still in flight (or not yet enqueued locally): its server id is
        // not recorded yet, so skip THIS supplement and advance to the next —
        // FIFO replays the parent first and this supplement pushes next run.
        continue;
      }
      await storage.markSupplementSending(row.clientSubmissionId);
      const res = await transport.createCompanySubmission(row);
      if (isRetryable(res.status)) {
        await storage.markSupplementRetry(
          row.clientSubmissionId,
          `company_submission_${res.status}`,
        );
        return false;
      }
      if (res.status === 200 || res.status === 201) {
        // Fresh 201 store or idempotent 200 replay — same row server-side.
        await storage.markSupplementSynced(row.clientSubmissionId);
        continue;
      }
      await storage.markSupplementError(row.clientSubmissionId, `company_submission_${res.status}`);
    }
    return true;
  }

  // FIFO over the set rows, re-queried each hop like the submission drain: a
  // queued set is created (an unchecked intake is checked first), a set
  // uploading evidence drains its files. A (row, state) pair seen twice made no
  // progress this run and waits for the next; `needs_answer`, `blocked` and
  // `error` rows are never picked. A retryable create or upload ends the run
  // offline; a retryable CHECK does not — its row stays queued, unsent.
  async function pushChangeRequestSets(): Promise<boolean> {
    const seen = new Set<string>();
    for (;;) {
      const row = (await storage.listChangeRequestSetRows()).find(
        (r) =>
          (r.state === "queued" || r.state === "uploading_evidence") &&
          !seen.has(`${r.clientSetId}:${r.state}`),
      );
      if (row === undefined) return true;
      seen.add(`${row.clientSetId}:${row.state}`);
      if (row.state === "uploading_evidence") {
        if (!(await pushSetUploads(row))) return false;
        continue;
      }
      const checkRequest = candidateCheckRequest(row);
      const sendable = checkRequest === null ? row : await checkCandidates(row, checkRequest);
      if (sendable !== null && !(await postChangeRequestSet(sendable))) return false;
    }
  }

  // The at-send candidate check. Answers the row to send — re-read, so the
  // create carries the `candidates_checked_at` just stamped into its body — or
  // null when the set must not be sent this run.
  async function checkCandidates(
    row: OutboxChangeRequestSetRow,
    request: IntakeCandidatesRequest,
  ): Promise<OutboxChangeRequestSetRow | null> {
    const res = await transport.checkIntakeCandidates(request);
    if (isRetryable(res.status)) {
      await storage.markChangeRequestSetCheckFailed(row.clientSetId);
      return null;
    }
    if (res.status !== 200 || res.data === undefined) {
      await storage.markChangeRequestSetError(
        row.clientSetId,
        `intake_candidates_http_${res.status}`,
      );
      return null;
    }
    if (res.data.length > 0) {
      await storage.markChangeRequestSetNeedsAnswer(row.clientSetId, res.data);
      return null;
    }
    await storage.markChangeRequestSetChecked(row.clientSetId);
    const rows = await storage.listChangeRequestSetRows();
    return rows.find((r) => r.clientSetId === row.clientSetId) ?? null;
  }

  // POST a queued set; its receipt moves it to its uploads (or straight to
  // received). Returns false when connectivity is gone.
  async function postChangeRequestSet(row: OutboxChangeRequestSetRow): Promise<boolean> {
    await storage.markChangeRequestSetSending(row.clientSetId);
    const res = await transport.createChangeRequestSet(row);
    const outcome = decideChangeRequestSetOutcome(res.status, res.data, res.error);
    switch (outcome.next) {
      case "uploading_evidence":
      case "received":
        await storage.storeChangeRequestSetReceipt(row.clientSetId, outcome.receipt);
        return true;
      case "blocked":
        await storage.markChangeRequestSetBlocked(row.clientSetId, outcome.failing);
        return true;
      case "error":
        await storage.markChangeRequestSetError(row.clientSetId, outcome.lastError);
        return true;
      case "retry":
        await storage.markChangeRequestSetRetry(row.clientSetId, outcome.lastError);
        return false;
    }
  }

  // Upload a standalone set's staged evidence to the set its receipt named;
  // the upload that answers the set complete moves it to received.
  async function pushSetUploads(row: OutboxChangeRequestSetRow): Promise<boolean> {
    const setId = row.receipt?.set_id;
    const toSend = uploadsToSend(await storage.listAttachmentsForChangeRequestSet(row.clientSetId));
    if (setId === undefined || toSend.length === 0) {
      await storage.markChangeRequestSetError(
        row.clientSetId,
        setId === undefined
          ? "uploading_evidence_without_receipt"
          : "uploading_evidence_without_rows",
      );
      return true;
    }
    for (const attachment of toSend) {
      await storage.markAttachmentSending(attachment.clientAttachmentId);
      const evidence = await storage.readEvidencePart(attachment);
      const res = await transport.uploadChangeRequestAttachment(setId, attachment, evidence);
      if (isRetryable(res.status)) return false;
      if ((res.status === 200 || res.status === 201) && res.data !== undefined) {
        await storage.markChangeRequestSetAttachmentUploaded(
          attachment.clientAttachmentId,
          res.data.set_attachments_complete,
        );
        continue;
      }
      await storage.markChangeRequestSetError(row.clientSetId, `upload_http_${res.status}`);
      return true;
    }
    return true;
  }

  // POST the finalized document on the row's snapshotted stack. Returns false
  // when connectivity is gone (the row reverts to finalized, attempts++).
  async function postSubmission(row: OutboxSubmissionRow): Promise<boolean> {
    await storage.markSending(row.clientSubmissionId);
    const res = await transport.createSubmission(row);
    const attachments = await storage.listAttachmentsForSubmission(row.clientSubmissionId);
    const outcome = decidePostOutcome(res.status, res.data, attachments.length, res.error);
    await storage.applyPostOutcome(row.clientSubmissionId, outcome);
    return outcome !== null;
  }

  // Upload staged evidence for an attachments_pending row, each file by its
  // target: a form file to the submission's route, a riding request's file to
  // its set's (`serverChangeRequestSetId`). Either answer carries the
  // submission's post-gate truth into markAttachmentUploaded, so the item
  // completes from whichever upload lands last. When nothing is left to send,
  // the last file is replayed (per-file idempotent 200) so an interrupted parent
  // transition completes without a GET op on the field surface.
  async function pushUploads(row: OutboxSubmissionRow): Promise<boolean> {
    const serverSubmissionId = row.serverSubmissionId;
    if (serverSubmissionId === null) {
      await storage.applyPostOutcome(row.clientSubmissionId, {
        next: "error",
        lastError: "attachments_pending_without_server_id",
      });
      return true;
    }
    const toSend = uploadsToSend(
      await storage.listAttachmentsForSubmission(row.clientSubmissionId),
    );
    const setId = row.serverChangeRequestSetId;
    const missing =
      toSend.length === 0
        ? "attachments_pending_without_rows"
        : setId === null && toSend.some((a) => a.target === "change_request_set")
          ? "attachments_pending_without_change_request_set_id"
          : null;
    if (missing !== null) {
      await storage.applyPostOutcome(row.clientSubmissionId, {
        next: "error",
        lastError: missing,
        serverSubmissionId,
        serverChangeRequestSetId: setId ?? undefined,
      });
      return true;
    }
    for (const attachment of toSend) {
      await storage.markAttachmentSending(attachment.clientAttachmentId);
      const evidence = await storage.readEvidencePart(attachment);
      // The guard above parked a set-target file with no set id to route to.
      const res =
        attachment.target === "change_request_set" && setId !== null
          ? await uploadRequestFile(setId, attachment, evidence)
          : await uploadFormFile(row, serverSubmissionId, attachment, evidence);
      if (isRetryable(res.status)) return false;
      if ((res.status === 200 || res.status === 201) && res.completion !== undefined) {
        await storage.markAttachmentUploaded(
          attachment.clientAttachmentId,
          res.completion.submissionStatus,
          res.completion.complete,
        );
        continue;
      }
      // Deterministic rejection (422 not-referenced, 409 immutable, 413 …):
      // the parent parks in error with the cause; staged evidence is retained.
      await storage.applyPostOutcome(row.clientSubmissionId, {
        next: "error",
        lastError: `upload_http_${res.status}`,
        serverSubmissionId,
        serverChangeRequestSetId: setId ?? undefined,
      });
      return true;
    }
    return true;
  }

  async function uploadFormFile(
    row: OutboxSubmissionRow,
    serverSubmissionId: string,
    attachment: OutboxAttachmentRow,
    evidence: { part: EvidencePart; filename: string },
  ): Promise<UploadAnswer> {
    const res = await transport.uploadAttachment(
      row.stack,
      row.orgId,
      serverSubmissionId,
      attachment,
      evidence,
    );
    return {
      status: res.status,
      completion:
        res.data === undefined
          ? undefined
          : {
              submissionStatus: res.data.submission_status,
              complete: res.data.attachments_complete,
            },
    };
  }

  async function uploadRequestFile(
    setId: string,
    attachment: OutboxAttachmentRow,
    evidence: { part: EvidencePart; filename: string },
  ): Promise<UploadAnswer> {
    const res = await transport.uploadChangeRequestAttachment(setId, attachment, evidence);
    // A submission-anchored set always answers both members; a null reads as
    // not complete, which leaves the item waiting on its other uploads.
    return {
      status: res.status,
      completion:
        res.data === undefined
          ? undefined
          : {
              submissionStatus: res.data.submission_status ?? "",
              complete: res.data.submission_attachments_complete === true,
            },
    };
  }

  // Local-only I/O: acknowledged rows lose their staged files + draft, then hop
  // to synced. Failures leave the row acknowledged for the next run — cleanup
  // fails and retries independently of the push pipeline. Revoked and blocked
  // rows are NEVER cleaned: their draft + evidence are retained and flagged in
  // the UI, and a blocked row's capture is what resubmits once `retryBlocked`
  // returns it to the queue.
  async function cleanupAcked(): Promise<void> {
    const rows = await storage.listVisible();
    for (const row of rows) {
      if (row.state !== "accepted" && row.state !== "needs_review") continue;
      try {
        await storage.deleteEvidenceForAssignment(row.assignmentId);
        await storage.deleteDraft(row.assignmentId);
        await storage.markCleanedUp(row.clientSubmissionId);
      } catch (err) {
        console.warn("fieldwork evidence cleanup failed", err);
      }
    }
    // A received set, like an acked test: its own bytes and draft rows, then
    // the terminal hop, which clears its staged tests' owners and its sidecar.
    for (const set of await storage.listChangeRequestSetRows()) {
      if (set.state !== "received") continue;
      try {
        await storage.deleteEvidenceForAssignment(set.clientSetId);
        await storage.deleteDraft(set.clientSetId);
        await storage.markChangeRequestSetSynced(set.clientSetId);
      } catch (err) {
        console.warn("fieldwork change-request cleanup failed", err);
      }
    }
  }

  // Wholesale-replace the local snapshot with the scope's bundle and record the
  // server pull timestamp; a field pull then brings the catalogs up to the
  // bundle's tokens. Deterministic non-200s (401/403) skip the replace without
  // flagging the run offline.
  async function pullBundle(scope: SyncScope): Promise<{ pulled: boolean; offline: boolean }> {
    const res = await transport.getBundle(scope);
    if (isRetryable(res.status)) return { pulled: false, offline: true };
    if (res.status !== 200 || res.data === undefined) return { pulled: false, offline: false };
    if (scope.mode === "field") {
      await storage.replaceBundle({ kind: "field" }, res.data);
    } else {
      await storage.replaceBundle({ kind: "org", orgId: scope.orgId }, res.data);
    }
    await storage.setSyncMeta(syncMetaKey(scope), res.data.pulled_at);
    const online = scope.mode === "field" ? await pullCatalogs(res.data.catalog_versions) : true;
    return { pulled: true, offline: !online };
  }

  // One compare per field pull: a stored catalog of an org the bundle no
  // longer names is removed, and each org whose bundle token differs from the
  // stored raw etag gets one conditional GET — 304 leaves the stored document
  // untouched, 200 replaces it under the document's own `version` (the raw
  // token). Returns false when connectivity is gone.
  async function pullCatalogs(versions: Record<string, string>): Promise<boolean> {
    const stored = new Map((await storage.list()).map((c) => [c.orgId, c.etag]));
    for (const orgId of stored.keys()) {
      if (versions[orgId] === undefined) await storage.remove(orgId);
    }
    for (const [orgId, token] of Object.entries(versions)) {
      const etag = stored.get(orgId) ?? null;
      if (etag === token) continue;
      const res = await transport.getCatalogSnapshot(orgId, etag);
      if (isRetryable(res.status)) return false;
      if (res.status === 200 && res.data !== undefined) {
        await storage.save(orgId, res.data.version, res.data);
      }
    }
    return true;
  }

  return { syncNow, syncFresh, subscribeSyncState, isSyncRunning };
}
